import { AppointmentSetup } from "../../../../models/site-setup.interface";
import { useSiteContext } from "../../../../context/siteContext";
import useApiService, { getCacheKey, invalidateCacheKey } from "../../../../hooks/useApiService";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import React, { useState } from "react";
import { Dialog } from "primereact/dialog";
import { MultiSelect } from "../../../../components/Shared/Forms";
import { Button } from "primereact/button";
import { CancelIcon, CheckIcon, Eye, PencilEdit, Trash } from "../../../../assets/icons";
import { Typography } from "@mui/joy";
import { DSTIRecord } from "../../../../models/DSTI/site-dsti-list.interface";
import { Dropdown } from "primereact/dropdown";

function Appointments() {
  const { site, getSite } = useSiteContext();
  const [modalToView, setModalToView] = useState<"assign-employee" | null>(null);
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentSetup | null>(null);

  const {
    data,
    loading,
    fetchData: fetchSiteSetup,
  } = useApiService<AppointmentSetup[]>({
    url: "site-setup",
    params: {
      siteId: site?.id || "",
    },
    method: "GET",
    apiName: "core",
  });

  const {
    data: employees,
    loading: loadingEmployees,
    fetchData: fetchEmployees,
  } = useApiService<{ full_name: string; id: string; name: string; surname: string }[]>({
    url: "employee-select",
    method: "GET",
    apiName: "core",
    fetchOnMount: false,
  });

  const { fetchData: assignEmployee, loading: assigningEmployee } = useApiService({
    url: "site-setup",
    method: "POST",
    apiName: "core",
    fetchOnMount: false,
    onSuccess: async () => {
      setModalToView(null);
      await fetchSiteSetup();
      await getSite(site?.id || "");
    },
  });

  async function handleRowClick(e: any) {
    const selectedAppointment: AppointmentSetup = e.data;
    setModalToView("assign-employee");
    await fetchEmployees({
      fetchParams: {
        appointmentId: selectedAppointment.id,
      },
    });
    setSelectedAppointment(e.data);
  }

  function handleSelectionChange(item: { label: string; value: string }) {
    const employees = selectedAppointment?.employee || [];
    const isSelected = employees.includes(item.value);
    if (isSelected) {
      setSelectedAppointment({
        ...selectedAppointment!,
        employee: employees.filter(emp => emp !== item.value),
      });
      return;
    }

    if (selectedAppointment) {
      setSelectedAppointment({
        ...selectedAppointment,
        employee: [...employees, item.value],
      });
    }
  }

  async function saveAssignment() {
    if (selectedAppointment) {
      await assignEmployee({
        payload: {
          employee: selectedAppointment.employee,
          appointment: selectedAppointment.id,
          site_id: site?.id,
        },
      });
    }
  }

  const renderAppointmentAssigned = (row: AppointmentSetup) => {
    if (row.appointment_assigned) {
      return <CheckIcon fill={"#2DD36FFF"} />;
    }
    return <CancelIcon fill={"#EB445AFF"} />;
  };

  const optionTemplate = (option: { label: string; value: string }) => {
    switch (option.value) {
      case "view":
        return (
          <div className="flex align-items-center gap-2" style={{ minWidth: "4rem" }}>
            <Eye />
            <div>{option.label}</div>
          </div>
        );
      case "edit":
        return (
          <div className="flex align-items-center gap-2" style={{ minWidth: "4rem" }}>
            <PencilEdit />
            <div>{option.label}</div>
          </div>
        );

      case "delete":
        return (
          <div className="flex align-items-center gap-2" style={{ minWidth: "4rem" }}>
            <Trash />
            <div>{option.label}</div>
          </div>
        );
      default:
        return (
          <div className="flex align-items-center gap-2" style={{ minWidth: "4rem" }}>
            <div>{option.label}</div>
          </div>
        );
    }
  };

  const displaySettings = (rowData: AppointmentSetup) => {
    return (
      //   e.stopPropagation is used
      //   to prevent the row click event from being triggered when clicking on the settings icon
      <div onClick={e => e.stopPropagation()}>
        <Dropdown
          options={[{ label: "Update appointment", value: "edit", id: rowData.id, name: "name" }]}
          dropdownIcon={"pi pi-cog"}
          placeholder=" "
          className="p-dropdown-icon-only"
          style={{
            border: "none",
            background: "transparent",
            boxShadow: "none",
            minWidth: "auto",
          }}
          showClear={false}
          onChange={async () => {
            console.log(rowData);
            const selectedAppointment: AppointmentSetup = rowData;
            setModalToView("assign-employee");
            await fetchEmployees({
              fetchParams: {
                appointmentId: selectedAppointment.id,
              },
            });
            setSelectedAppointment(rowData);
          }}
          itemTemplate={optionTemplate}
          panelStyle={{ minWidth: "350px" }}
        />
      </div>
    );
  };

  return (
    <div>
      <Typography level="h2" component="h1">
        Appointments: {site?.name}
      </Typography>
      <div className={"my-4"}>
        <DataTable
          loading={loading}
          value={data ?? []}
          className={`policies-procedures-table`}
          tableStyle={{ minWidth: "50rem" }}
          onRowClick={handleRowClick}
        >
          <Column field={"name"} header={"Appointment Name"} style={{ minWidth: "12rem" }} />
          <Column
            field={"legal_reference"}
            header={"Legal Reference"}
            style={{ minWidth: "12rem" }}
          />
          <Column field={"type"} header={"Type"} style={{ minWidth: "12rem" }} />
          <Column field={"type_name"} header={"Item"} style={{ minWidth: "12rem" }} />
          <Column
            field={"appointment_assigned"}
            header={"Appointment Assigned"}
            body={renderAppointmentAssigned}
            align={"center"}
          />
          <Column field={"employee_list"} header={"Employee"} />
          <Column field={"_"} header={"Settings"} body={displaySettings} />
        </DataTable>
      </div>

      <Dialog
        onHide={() => setModalToView(null)}
        header={"Assign Appointment"}
        visible={!!modalToView && !!selectedAppointment?.id}
        style={{ width: "50vw", maxWidth: "760px" }}
        modal
      >
        <div className={"mt-4"}>
          <MultiSelect
            options={
              employees?.map(employee => ({
                label: employee.full_name,
                value: employee.id,
              })) || []
            }
            label={"Select Employees"}
            value={selectedAppointment?.employee || []}
            onChange={(val: any) => {
              handleSelectionChange(val.selectedOption as { label: string; value: string });
            }}
            loading={loadingEmployees}
            onSelectAll={(val: any) => {
              if (selectedAppointment) {
                setSelectedAppointment({
                  ...selectedAppointment,
                  employee: val,
                });
              }
            }}
          />
          {
            employees?.length === 0 && loadingEmployees === false && (
              <div style={{
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                padding: '16px',
                marginTop: '16px',
                borderLeft: '4px solid #007bff'
              }}>
                <p style={{
                  margin: '0 0 12px 0',
                  fontWeight: '600',
                  color: '#495057'
                }}>
                  No Competent Employee Found for this Appointment
                </p>
                <p style={{
                  margin: '0 0 12px 0',
                  color: '#6c757d'
                }}>
                  There are currently no employees on this site who meet the competency requirements for this appointment.
                </p>
                <p style={{
                  margin: '0 0 8px 0',
                  color: '#495057'
                }}>
                  Go back to your Main Employee Database in your Admin Portal to:
                </p>
                <ul style={{
                  margin: '0 0 12px 0',
                  paddingLeft: '20px',
                  color: '#6c757d'
                }}>
                  <li style={{ marginBottom: '4px' }}>
                    Assign this appointment to an employee who will be working on this site, or
                  </li>
                  <li>
                    Add a new competent employee to this site for this appointment.
                  </li>
                </ul>
                <p style={{
                  margin: '0',
                  color: '#495057',
                  fontStyle: 'italic'
                }}>
                  Once updated, return to this page to select the employee.
                </p>
              </div>
            )
          }
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%",
              gap: "1rem",
              background: "white",
              paddingTop: "1rem",
              borderTop: "1px solid #e5e7eb",
              flexShrink: 0,
            }}
          >
            <Button
              color={"primary"}
              onClick={async () => {
                await saveAssignment();
              }}
              label={"Save"}
              loading={assigningEmployee}
              disabled={assigningEmployee}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
}

export default Appointments;
